import { transformExamCenterValidationErrors } from '@exam-center/exam-center-management';
import {
  useAdminUpdateExamCenterMutation,
  GetMyExamCentersDocument,
  AdminGetExamCenterByExamCenterCodeDocument,
  GetExamCenterByExamCenterCodeDocument,
} from '@exam-center/graphql';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useForm } from 'react-hook-form';

import { ExamCenterDetailsForm } from '../../components';
import { useExamCenter } from '../../providers';

import {
  ExamCenterDetailsFormSchema,
  type ExamCenterDetailsFormInput,
} from './exam-center-detail-form-schema';
import { mapExamCenterDetailsDefaultFormValues } from './mapper';

export default function AdminExamCenterDetailsFormLogic() {
  const { examCenter } = useExamCenter();
  const { showSnackbar } = useSnackbar();
  const [updateExamCenter] = useAdminUpdateExamCenterMutation();

  const formContext = useForm({
    defaultValues: mapExamCenterDetailsDefaultFormValues(examCenter),
    resolver: zodResolver(ExamCenterDetailsFormSchema),
  });

  const handleSubmit = async (data: ExamCenterDetailsFormInput) => {
    await updateExamCenter({
      variables: {
        adminUpdateExamCenterInput: {
          examCenterCode: examCenter.code,
          nameTH: data.nameTH,
          nameEN: data.nameEN,
          addressTH: data.addressTH,
          addressEN: data.addressEN,
          phoneTH: data.phoneTH,
          phoneEN: data.phoneEN,
          email: data.email,
          examResultRecipientEmail: data.examResultRecipientEmail,
          website: data.website,
        },
      },
      refetchQueries: [
        GetMyExamCentersDocument,
        {
          query: AdminGetExamCenterByExamCenterCodeDocument,
          variables: {
            getExamCenterByExamCenterCodeInput: {
              examCenterCode: examCenter.code,
            },
          },
        },
        GetExamCenterByExamCenterCodeDocument,
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackbar({ message: 'บันทึกข้อมูลศูนย์ทดสอบเรียบร้อยแล้ว', variant: 'success' });
        formContext.reset(formContext.getValues());
      },
      onError: (error) => {
        showSnackbar({ message: 'เกิดข้อผิดพลาดในการแก้ไขข้อมูลศูนย์ทดสอบ', variant: 'error' });

        const validationErrors = transformExamCenterValidationErrors(error);
        const formValues = formContext.getValues();

        for (const validationError of validationErrors) {
          const propertyName = validationError.property;

          if (propertyName in formValues) {
            formContext.setError(propertyName as keyof ExamCenterDetailsFormInput, {
              type: 'custom',
              message: validationError.message,
            });
          }
        }
      },
    });
  };

  return (
    <ExamCenterDetailsForm code={examCenter.code} form={formContext} onSubmit={handleSubmit} />
  );
}
